/**
 * §notif-center — transient toast overlay.
 *
 * Watches the notifications list and pops a toast when a NEW one arrives (on the
 * first load it primes its "seen" set silently, so existing unread items don't
 * flood the screen). info/warning toasts auto-dismiss after a few seconds (with
 * a synced countdown bar); critical toasts stay until the operator closes them
 * and carry a subtle pulse. Clicking a toast opens its source; closing is local
 * — the notification remains in the bell + history.
 *
 * Mounted once inside AppShell so polling only runs on authenticated pages.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  listNotifications,
  markNotificationRead,
  notificationHref,
  type NotificationKind,
  type NotificationSeverity,
} from "@/lib/appNotifications";
import { notificationIcon } from "./icons";

interface Toast {
  id: string;
  kind: NotificationKind;
  severity: NotificationSeverity;
  title: string;
  body: string;
  href: string | null;
}

const AUTO_DISMISS_MS = 6500;

export function NotificationToaster() {
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seen = useRef<Set<string> | null>(null);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const q = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => listNotifications(50),
    refetchInterval: 15_000,
  });

  function removeToast(id: string) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timers.current[id];
    if (handle) {
      clearTimeout(handle);
      delete timers.current[id];
    }
  }

  useEffect(() => {
    if (!q.data) return;
    const items = q.data.notifications ?? [];
    // First load: remember what's already there; don't toast a backlog.
    if (seen.current === null) {
      seen.current = new Set(items.map((n) => n.id));
      return;
    }
    for (const n of items) {
      if (seen.current.has(n.id)) continue;
      seen.current.add(n.id);
      if (n.dismissed_at) continue;
      setToasts((prev) => [
        ...prev,
        {
          id: n.id,
          kind: n.kind,
          severity: n.severity,
          title: n.title,
          body: n.body,
          href: notificationHref(n),
        },
      ]);
      if (n.severity !== "critical") {
        timers.current[n.id] = setTimeout(() => removeToast(n.id), AUTO_DISMISS_MS);
      }
    }
  }, [q.data]);

  // Clear any pending timers on unmount.
  useEffect(
    () => () => {
      Object.values(timers.current).forEach(clearTimeout);
      timers.current = {};
    },
    [],
  );

  if (toasts.length === 0) return null;

  return (
    <div className="notif-toaster" aria-live="polite" aria-atomic="false">
      {toasts.map((t) => {
        const Icon = notificationIcon(t);
        const go = t.href
          ? () => {
              markNotificationRead(t.id).catch(() => {});
              navigate(t.href!);
              removeToast(t.id);
            }
          : undefined;
        return (
          <div
            key={t.id}
            className={`notif-toast notif-sev-${t.severity}` + (t.href ? " is-clickable" : "")}
            role="status"
          >
            <span className="notif-chip" aria-hidden>
              <Icon size={16} strokeWidth={2} />
            </span>
            <div
              className="notif-toast-main"
              role={t.href ? "button" : undefined}
              tabIndex={t.href ? 0 : undefined}
              onClick={go}
              onKeyDown={
                go
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        go();
                      }
                    }
                  : undefined
              }
            >
              <div className="notif-toast-title">{t.title}</div>
              {t.body && <div className="notif-toast-body">{t.body}</div>}
            </div>
            <button
              type="button"
              className="notif-toast-close"
              aria-label="Dismiss"
              onClick={(e) => {
                e.stopPropagation();
                removeToast(t.id);
              }}
            >
              <X size={14} aria-hidden />
            </button>
            {t.severity !== "critical" && (
              <span
                className="notif-toast-progress"
                style={{ animationDuration: `${AUTO_DISMISS_MS}ms` }}
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
